/**
 * O formulário de uma ação do plano (issue #359).
 *
 * É diálogo e não página: a pessoa trabalha percorrendo a tabela, e sair dela custa o lugar na
 * lista. Os campos vêm na ordem da conversa — o que fazer, para quando e com quem, de onde
 * veio, e como se comprova.
 *
 * O prazo não aparece como campo em lugar nenhum: ele sai da prioridade mais a data de
 * elaboração do plano, e quem o calcula é o servidor (D3).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AttachFile as AnexoIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { useVeredito } from '../../../authorization/useVeredito';
import { pode } from '../../../authorization/pode';
import { riskApplicationService } from '../../../services/riskApplicationService';
import type { RiskApplication } from '../../../types/riskApplication';
import {
  RISK_LEVEL_DECISIONS,
  calculateRiskLevel,
  pickCompleteMatrix,
} from '../../../types/riskMatrix';
import {
  ORIGENS,
  PRIORIDADES,
  RESPONSAVEIS,
  ROTULO_DA_EVIDENCIA,
  ROTULO_DA_ORIGEM,
  ROTULO_DA_PRIORIDADE,
  ROTULO_DA_SITUACAO,
  ROTULO_DO_RESPONSAVEL,
  ROTULO_DO_TIPO_DE_ACAO,
  SITUACOES_DA_ACAO,
  TIPOS_DE_ACAO,
  TIPOS_DE_EVIDENCIA,
  VIGENCIA_DA_PRIORIDADE,
  type ActionItemStatus,
  type ActionPlanItem,
  type ActionPriority,
  type ActionResponsible,
  type ActionType,
  type DadosDaAcao,
  type EvidenceType,
  type OriginType,
} from '../../../types/actionPlan';

export interface ActionPlanItemDialogProps {
  aberto: boolean;
  /** Nulo é ação nova; preenchido é edição. */
  item: ActionPlanItem | null;
  /** De quem são os riscos oferecidos no seletor de origem. */
  companyId: string;
  salvando?: boolean;
  onFechar: () => void;
  onSalvar: (dados: DadosDaAcao, evidencia: File | null) => void;
}

const VAZIO: DadosDaAcao = {
  description: '',
  actionType: 'INTRODUZIR',
  norma: null,
  priority: 'DE_ATENCAO',
  responsible: 'EMPREGADOR',
  responsibleName: null,
  status: 'PLANEJADA',
  evidenceType: null,
  evidenceNotes: null,
  originType: 'MANUAL',
  originId: null,
};

/** Vazio vira nulo, sem aparar: aparar a cada tecla impede a pessoa de digitar a segunda palavra. */
function comoDigitado(valor: string): string | null {
  return valor === '' ? null : valor;
}

function nomeDoRisco(aplicacao: RiskApplication): string {
  return aplicacao.risk?.riskFactorName ?? `Risco #${aplicacao.riskId}`;
}

/**
 * A prioridade que o nível de risco da aplicação escolhida manda (#418).
 *
 * Nula quando a matriz está incompleta — a mesma lacuna que deixa a coluna do inventário
 * vazia no documento, e que aqui vira o aviso ao lado do campo em vez de um palpite.
 */
function prioridadeDoRisco(aplicacao: RiskApplication | undefined): ActionPriority | null {
  const { riskMatrixType, riskProbability, riskSeverity } = pickCompleteMatrix(aplicacao);
  if (riskMatrixType === null || riskProbability === null || riskSeverity === null) {
    return null;
  }

  const nivel = calculateRiskLevel(riskMatrixType, riskProbability, riskSeverity);
  return nivel === null ? null : RISK_LEVEL_DECISIONS[nivel].priority;
}

export const ActionPlanItemDialog: React.FC<ActionPlanItemDialogProps> = ({
  aberto,
  item,
  companyId,
  salvando = false,
  onFechar,
  onSalvar,
}) => {
  const [dados, setDados] = useState<DadosDaAcao>(VAZIO);
  const [evidencia, setEvidencia] = useState<File | null>(null);
  const [faltaEvidencia, setFaltaEvidencia] = useState(false);
  const [riscoSemMatriz, setRiscoSemMatriz] = useState<RiskApplication | null>(null);

  // O atalho leva para o cadastro do risco, que é tela de outro módulo comercial:
  // oferecê-lo a quem não o contratou é oferecer uma recusa.
  const acessoAoRisco = useVeredito(
    '/occupational-risks/:id/edit',
    pode('occupational_risk.update')
  );

  useEffect(() => {
    if (!aberto) return;
    setFaltaEvidencia(false);
    setRiscoSemMatriz(null);
    setEvidencia(null);
    setDados(
      item
        ? {
            description: item.description,
            actionType: item.actionType,
            norma: item.norma,
            priority: item.priority,
            responsible: item.responsible,
            responsibleName: item.responsibleName,
            status: item.status,
            evidenceType: item.evidenceType,
            evidenceNotes: item.evidenceNotes,
            originType: item.originType,
            originId: item.originId,
          }
        : VAZIO
    );
  }, [aberto, item]);

  const carregarRiscos = useCallback(
    () => riskApplicationService.list('company', Number(companyId)),
    [companyId]
  );

  /**
   * O seletor de risco some quando a empresa não tem nenhum, em vez de abrir vazio — e quem não
   * pode ler os riscos aplicados nem chega a pedi-los: a recusa não alimentaria campo nenhum e
   * só deixava erro no console a cada abertura.
   */
  const riscos = useListaPermitida<RiskApplication>(
    'risk_application.read',
    'Riscos aplicados',
    carregarRiscos,
    { habilitado: aberto && companyId !== '' }
  );

  /**
   * Escolher ou trocar o risco reescreve a prioridade, sempre: o campo nunca está em branco,
   * então não há como distinguir o valor com que a ação abriu de uma escolha deliberada. O que
   * a pessoa editar depois fica, porque só a escolha do risco reescreve.
   */
  const escolherRisco = useCallback(
    (originId: string | null) => {
      const aplicacao = riscos.itens.find((item) => String(item.id) === originId);
      const herdada = prioridadeDoRisco(aplicacao);

      setRiscoSemMatriz(aplicacao !== undefined && herdada === null ? aplicacao : null);
      setDados((atual) => ({ ...atual, originId, priority: herdada ?? atual.priority }));
    },
    [riscos.itens]
  );

  const origensOferecidas = useMemo(
    () => ORIGENS.filter((origem) => origem !== 'RISCO' || riscos.itens.length > 0),
    [riscos.itens.length]
  );

  const concluida = dados.status === 'CONCLUIDA';

  const salvar = useCallback(() => {
    if (dados.description.trim() === '') return;
    // Concluir sem evidência é pré-requisito ausente: o campo fica aqui mesmo, à vista.
    if (concluida && dados.evidenceType === null) {
      setFaltaEvidencia(true);
      return;
    }
    setFaltaEvidencia(false);
    onSalvar(dados, evidencia);
  }, [concluida, dados, evidencia, onSalvar]);

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="md">
      <DialogTitle>{item ? 'Editar ação' : 'Nova ação'}</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              A ação
            </Typography>
            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              label="O que será feito"
              value={dados.description}
              onChange={(e) => setDados({ ...dados, description: e.target.value })}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl required sx={{ minWidth: 180 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={dados.actionType}
                  onChange={(e) =>
                    setDados({ ...dados, actionType: e.target.value as ActionType })
                  }
                >
                  {TIPOS_DE_ACAO.map((tipo) => (
                    <MenuItem key={tipo} value={tipo}>
                      {ROTULO_DO_TIPO_DE_ACAO[tipo]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Norma"
                placeholder="NR 5 - CIPA"
                helperText="A norma que exige esta ação, como sai no documento"
                value={dados.norma ?? ''}
                onChange={(e) => setDados({ ...dados, norma: comoDigitado(e.target.value) })}
              />
            </Stack>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Prazo e responsável
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl required sx={{ minWidth: 220 }}>
                <InputLabel id="acao-prioridade-rotulo">Prioridade</InputLabel>
                <Select
                  labelId="acao-prioridade-rotulo"
                  label="Prioridade"
                  value={dados.priority}
                  onChange={(e) =>
                    setDados({ ...dados, priority: e.target.value as ActionPriority })
                  }
                >
                  {PRIORIDADES.map((prioridade) => (
                    <MenuItem key={prioridade} value={prioridade}>
                      {ROTULO_DA_PRIORIDADE[prioridade]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Alert severity="info" sx={{ flexGrow: 1 }}>
                Vigência {VIGENCIA_DA_PRIORIDADE[dados.priority]} — o prazo sai daí e da data de
                elaboração do plano, calculado ao salvar.
              </Alert>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl required sx={{ minWidth: 280 }}>
                <InputLabel>Responsável</InputLabel>
                <Select
                  label="Responsável"
                  value={dados.responsible}
                  onChange={(e) =>
                    setDados({ ...dados, responsible: e.target.value as ActionResponsible })
                  }
                >
                  {RESPONSAVEIS.map((papel) => (
                    <MenuItem key={papel} value={papel}>
                      {ROTULO_DO_RESPONSAVEL[papel]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Nome de quem responde"
                helperText="Opcional — o papel é o que sai no documento"
                value={dados.responsibleName ?? ''}
                onChange={(e) =>
                  setDados({ ...dados, responsibleName: comoDigitado(e.target.value) })
                }
              />
            </Stack>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              De onde veio
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel id="acao-origem-rotulo">Origem</InputLabel>
                <Select
                  labelId="acao-origem-rotulo"
                  label="Origem"
                  value={dados.originType}
                  onChange={(e) => {
                    setRiscoSemMatriz(null);
                    setDados({
                      ...dados,
                      originType: e.target.value as OriginType,
                      originId: null,
                    });
                  }}
                >
                  {origensOferecidas.map((origem) => (
                    <MenuItem key={origem} value={origem}>
                      {ROTULO_DA_ORIGEM[origem]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {dados.originType === 'RISCO' && (
                <FormControl fullWidth>
                  <InputLabel id="acao-risco-rotulo">Risco aplicado</InputLabel>
                  <Select
                    labelId="acao-risco-rotulo"
                    label="Risco aplicado"
                    value={dados.originId ?? ''}
                    onChange={(e) => escolherRisco(comoDigitado(String(e.target.value)))}
                  >
                    {riscos.itens.map((aplicacao) => (
                      <MenuItem key={aplicacao.id} value={String(aplicacao.id)}>
                        {nomeDoRisco(aplicacao)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
            {riscoSemMatriz !== null && (
              <Alert severity="info">
                {nomeDoRisco(riscoSemMatriz)} não tem a matriz de risco completa — falta o tipo
                de matriz, a probabilidade ou a severidade, e sem os três não há nível de que
                derivar a prioridade. Ela ficou como estava: escolha à mão
                {acessoAoRisco.liberado ? (
                  <>
                    , ou complete a matriz no{' '}
                    <Link
                      component={RouterLink}
                      to={`/occupational-risks/${riscoSemMatriz.riskId}/edit`}
                    >
                      cadastro do risco
                    </Link>
                    .
                  </>
                ) : (
                  '.'
                )}
              </Alert>
            )}
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Acompanhamento
            </Typography>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>Situação</InputLabel>
              <Select
                label="Situação"
                value={dados.status}
                onChange={(e) =>
                  setDados({ ...dados, status: e.target.value as ActionItemStatus })
                }
              >
                {SITUACOES_DA_ACAO.map((situacao) => (
                  <MenuItem key={situacao} value={situacao}>
                    {ROTULO_DA_SITUACAO[situacao]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {concluida && (
              <>
                <FormControl required error={faltaEvidencia} sx={{ minWidth: 280 }}>
                  <InputLabel>Evidência</InputLabel>
                  <Select
                    label="Evidência"
                    value={dados.evidenceType ?? ''}
                    onChange={(e) => {
                      setFaltaEvidencia(false);
                      setDados({
                        ...dados,
                        evidenceType: (e.target.value || null) as EvidenceType | null,
                      });
                    }}
                  >
                    {TIPOS_DE_EVIDENCIA.map((tipo) => (
                      <MenuItem key={tipo} value={tipo}>
                        {ROTULO_DA_EVIDENCIA[tipo]}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {faltaEvidencia
                      ? 'Falta a evidência: escolha o que comprova a conclusão antes de dar a ação por concluída.'
                      : 'Escolha o que comprova a conclusão — é o que fica registrado no documento.'}
                  </FormHelperText>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Observações da evidência"
                  value={dados.evidenceNotes ?? ''}
                  onChange={(e) =>
                    setDados({ ...dados, evidenceNotes: comoDigitado(e.target.value) })
                  }
                />

                <Box>
                  <Button component="label" variant="outlined" startIcon={<AnexoIcon />}>
                    Anexar arquivo
                    <input
                      hidden
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.bmp,.tif,.tiff"
                      onChange={(e) => setEvidencia(e.target.files?.[0] ?? null)}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    {evidencia?.name ??
                      item?.originalFileName ??
                      'PDF ou imagem, até 10 MB — opcional'}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button variant="contained" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActionPlanItemDialog;
